import React, { useState } from 'react';
import { Row, Button, Form, Collapse } from 'react-bootstrap';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import MoodBoardItemCard from './MoodBoardItemCard';

function MoodBoardGroup({
  group,
  items,
  allGroups,
  isUngrouped = false,
  editingItemId,
  editForm,
  onEditFormChange,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onDeleteItem,
  onImageClick,
  onRenameGroup,
  onDeleteGroup,
  onAddCard,
}) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');

  const groupId = group?.id || 'ungrouped';
  const groupName = group?.name || 'Ungrouped';

  const { setNodeRef, isOver } = useDroppable({ id: groupId });

  const handleStartRename = () => {
    setRenameValue(group.name);
    setIsRenaming(true);
  };

  const handleSaveRename = () => {
    if (renameValue.trim() && renameValue.trim() !== group.name) {
      onRenameGroup(group.id, renameValue.trim());
    }
    setIsRenaming(false);
  };

  const itemIds = items.map(item => item.id);

  return (
    <div className={`mood-board-group mb-4 ${isOver ? 'mood-board-group-over' : ''}`} ref={setNodeRef}>
      {/* Group Header */}
      <div className="mood-board-group-header d-flex align-items-center gap-2 mb-2">
        <Button
          variant="link"
          size="sm"
          className="p-0 text-muted"
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          <i className={`bi bi-chevron-${isCollapsed ? 'right' : 'down'}`}></i>
        </Button>

        {isRenaming ? (
          <div className="d-flex align-items-center gap-2 flex-grow-1">
            <Form.Control
              size="sm"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveRename();
                if (e.key === 'Escape') setIsRenaming(false);
              }}
              autoFocus
              style={{ maxWidth: 200 }}
            />
            <Button size="sm" variant="primary" onClick={handleSaveRename}>
              <i className="bi bi-check"></i>
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={() => setIsRenaming(false)}>
              <i className="bi bi-x"></i>
            </Button>
          </div>
        ) : (
          <>
            <h6
              className="mb-0 flex-grow-1"
              style={{ cursor: 'pointer' }}
              onClick={() => setIsCollapsed(!isCollapsed)}
            >
              {isUngrouped ? (
                <><i className="bi bi-collection me-2 text-muted"></i>{groupName}</>
              ) : (
                <><i className="bi bi-folder me-2 text-muted"></i>{groupName}</>
              )}
              <span className="badge bg-secondary ms-2" style={{ fontSize: '0.7rem' }}>
                {items.length}
              </span>
            </h6>
            {!isUngrouped && (
              <div className="mood-board-group-actions d-flex gap-1">
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 text-muted"
                  onClick={handleStartRename}
                  title="Rename group"
                >
                  <i className="bi bi-pencil" style={{ fontSize: '0.75rem' }}></i>
                </Button>
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 text-danger"
                  onClick={() => onDeleteGroup(group.id)}
                  title="Delete group"
                >
                  <i className="bi bi-trash" style={{ fontSize: '0.75rem' }}></i>
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Group Content */}
      <Collapse in={!isCollapsed}>
        <div>
          <SortableContext items={itemIds} strategy={rectSortingStrategy}>
            {items.length === 0 ? (
              <div className="mood-board-group-empty text-center text-muted py-3 border rounded">
                <i className="bi bi-arrows-move me-2"></i>
                <small>Drag items here</small>
              </div>
            ) : (
              <Row>
                {items.map((item) => (
                  <MoodBoardItemCard
                    key={item.id}
                    item={item}
                    isEditing={editingItemId === item.id}
                    editForm={editForm}
                    onEditFormChange={onEditFormChange}
                    onStartEdit={onStartEdit}
                    onSaveEdit={onSaveEdit}
                    onCancelEdit={onCancelEdit}
                    onDelete={onDeleteItem}
                    onImageClick={onImageClick}
                    groups={allGroups}
                  />
                ))}
              </Row>
            )}
          </SortableContext>
          {onAddCard && (
            <div className="mt-2">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={onAddCard}
              >
                <i className="bi bi-plus-lg me-1"></i>Add Card
              </Button>
            </div>
          )}
        </div>
      </Collapse>
    </div>
  );
}

export default MoodBoardGroup;
